import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { Button, Grid, Paper, TableContainer, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import * as api from '../../api/app/assetApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import DataGridTable from '../../components/core/DataGridTable/DataGridTable';
import { Loader } from '../../components/core/Loader/Loader';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { Asset, AssetType, assetTypes } from '../../types/app/asset.types';
import { STORAGE_KEYS } from '../../utils/core/localStorageKeys';
import { paths } from '../../utils/core/routes';
import { AssetListGroupSearch } from './AssetListGroupSearch';
interface Props {
  isReadOnly: boolean;
}

export function AssetList({ isReadOnly }: Props) {
  const queryClient = useQueryClient();
  const [selectedGroup, setSelectedGroup] = useState<number>();
  const [storageGroup, setStorageGroup] = useLocalStorage(STORAGE_KEYS.ASSET_GROUP, selectedGroup);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const { mutate: mutateDeleteAsset } = useMutation(api.deleteAsset);
  const { data, isLoading, isError } = useQuery<Asset[]>(['assets', selectedGroup], () =>
    api.getListFiltered(storageGroup ? storageGroup : selectedGroup),
  );

  useEffect(() => {
    queryClient.invalidateQueries(['assets']);
  }, [selectedGroup]);

  const columns = [
    {
      headerName: t('page.asset.table-col-1'),
      field: 'assetType',
      width: 250,
      renderCell: (t: any) =>
        assetTypes.map((type: AssetType) => {
          if (type.code === t.row.assetType) return type.name;
        }),
    },
    { headerName: t('page.asset.table-col-2'), field: 'name', width: 230 },
    { headerName: t('page.asset.table-col-3'), field: 'influxMachineName', width: 230 },
    { headerName: t('page.asset.table-col-4'), field: 'groupName', width: 230 },
    {
      field: '',
      sortable: false,
      disableColumnMenu: true,
      width: 250,
      flexGrow: 1,
      align: 'right',
      renderCell: (params: any) => {
        return (
          <>
            {isReadOnly && (
              <Tooltip title={t('general.action-buttons.detail')}>
                <Button onClick={() => handleOnEdit(params.id)}>
                  <RemoveRedEyeOutlinedIcon />
                </Button>
              </Tooltip>
            )}
            {!isReadOnly && (
              <>
                <Tooltip title={t('general.action-buttons.edit')}>
                  <Button onClick={() => handleOnEdit(params.id)}>
                    <EditIcon />
                  </Button>
                </Tooltip>
                <Tooltip title={t('general.action-buttons.delete')}>
                  <Button onClick={() => handleOnDelete(params.id)}>
                    <DeleteIcon />
                  </Button>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
  ];

  const handleOnCreate = () => navigate(paths.ASSETS_CREATE);
  const handleOnEdit = (id: number) => navigate(`${paths.ASSETS_DETAIL}/${id}`);
  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeleteAsset(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(t('page.asset.item-deleted'), { variant: 'success' });
        queryClient.invalidateQueries(['assets']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data) return <Loader />;

  return (
    <>
      <Grid item xs={12}>
        <AssetListGroupSearch
          setSelectedGroup={setSelectedGroup}
          setStorageGroup={setStorageGroup}
          storageGroup={storageGroup}
        />
        <TableContainer component={Paper}>
          <div style={{ height: '55vh', width: '100%' }}>
            <DataGridTable
              height={'55vh'}
              data={data}
              columns={columns}
              fulltextSearch={true}
              autoPageSize={true}
            />
          </div>
          {!isReadOnly && (
            <Tooltip title={t('general.action-buttons.new')}>
              <Button onClick={handleOnCreate}>
                <AddIcon />
              </Button>
            </Tooltip>
          )}
        </TableContainer>
      </Grid>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
